/**
 * React Query hooks for the user-configurable judge-sheet templates.
 * Backs /api/judge-sheet-templates-management (see judge-sheet-templates-management-workflow.ts).
 * The "-management" suffix on the prefix is intentional: the auto-generated CRUD
 * for the same table lives at /api/judge-sheet-templates (no auth, no is_system
 * enforcement) and would silently shadow the policy-enforced handlers if both
 * registered at the same prefix.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../http-client';
import type { JudgeSheetColumn } from '@/lib/judge-sheet-templates';

export interface JudgeSheetTemplateRecord {
  id: string;
  slug: string;
  display_name: string;
  description: string | null;
  columns: JudgeSheetColumn[];
  min_rows: number;
  is_system: boolean;
  organization_id: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface JudgeSheetTemplateCreateInput {
  slug: string;
  display_name: string;
  description?: string | null;
  columns: JudgeSheetColumn[];
  min_rows?: number;
  organization_id?: string | null;
}

export interface JudgeSheetTemplateUpdateInput {
  display_name?: string;
  description?: string | null;
  columns?: JudgeSheetColumn[];
  min_rows?: number;
  organization_id?: string | null;
}

export const judgeSheetTemplatesKeys = {
  all: ['judge-sheet-templates'] as const,
  list: () => [...judgeSheetTemplatesKeys.all, 'list'] as const,
  detail: (id: string) => [...judgeSheetTemplatesKeys.all, 'detail', id] as const,
};

export function useJudgeSheetTemplates() {
  return useQuery({
    queryKey: judgeSheetTemplatesKeys.list(),
    queryFn: async () => {
      const res = await httpClient.get<{ templates: JudgeSheetTemplateRecord[] }>(
        '/judge-sheet-templates-management',
      );
      return res.templates;
    },
  });
}

export function useJudgeSheetTemplate(id: string | null | undefined) {
  return useQuery({
    queryKey: judgeSheetTemplatesKeys.detail(id ?? ''),
    enabled: !!id,
    queryFn: async () => {
      const res = await httpClient.get<{ template: JudgeSheetTemplateRecord }>(
        `/judge-sheet-templates-management/${id}`,
      );
      return res.template;
    },
  });
}

export function useCreateJudgeSheetTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: JudgeSheetTemplateCreateInput) => {
      const res = await httpClient.post<{ template: JudgeSheetTemplateRecord }>(
        '/judge-sheet-templates-management',
        input,
      );
      return res.template;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: judgeSheetTemplatesKeys.list() });
    },
  });
}

export function useUpdateJudgeSheetTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; input: JudgeSheetTemplateUpdateInput }) => {
      const res = await httpClient.put<{ template: JudgeSheetTemplateRecord }>(
        `/judge-sheet-templates-management/${args.id}`,
        args.input,
      );
      return res.template;
    },
    onSuccess: (template) => {
      qc.invalidateQueries({ queryKey: judgeSheetTemplatesKeys.list() });
      qc.invalidateQueries({ queryKey: judgeSheetTemplatesKeys.detail(template.id) });
    },
  });
}

export function useDeleteJudgeSheetTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await httpClient.delete(`/judge-sheet-templates-management/${id}`);
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: judgeSheetTemplatesKeys.list() });
    },
  });
}
